import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { waitForDatabaseUserId } from "../services/CurrentUser";
import {
    GetAvailableRoles,
    GetUserPreferences,
    toUserPreferences,
    UpdateUserPreferences,
    UpdateUserPreferencesResult,
    UserPreferences,
    UserPreferencesUpdate,
} from "../services/UserPreferencesService";

export type UserPreferencesStatus = "loading" | "ready" | "unavailable";

interface UserPreferencesContextType {
    preferences: UserPreferences | null;
    /** Enabled role names the user can pick from. */
    roles: string[];
    status: UserPreferencesStatus;
    isSettingsOpen: boolean;
    openSettings: () => void;
    closeSettings: () => void;
    /** Saves the changes and, on success, updates `preferences` from the server response. */
    save: (update: UserPreferencesUpdate) => Promise<UpdateUserPreferencesResult>;
    /** Loads the preferences again, e.g. after a failure. */
    reload: () => Promise<void>;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | null>(null);

// Each Teams tab (and browser tab) is its own page, so preferences changed in one are picked up by the others
// through a broadcast where supported, and otherwise when the page is shown or focused again.
const REFRESH_INTERVAL_MS = 30000;
const MIN_REFRESH_GAP_MS = 2000;
const BROADCAST_CHANNEL_NAME = "howru-user-preferences";

const UNAVAILABLE_MESSAGE = "Your user account is not available right now. Please try again later.";

interface PreferencesBroadcast {
    userId: string;
    preferences: unknown;
}

const createBroadcastChannel = (): BroadcastChannel | null => {
    try {
        return typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    } catch {
        return null;
    }
};

export const UserPreferencesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [preferences, setPreferences] = useState<UserPreferences | null>(null);
    const [roles, setRoles] = useState<string[]>([]);
    const [status, setStatus] = useState<UserPreferencesStatus>("loading");
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    const userIdRef = useRef("");
    const rolesRef = useRef<string[]>([]);
    const statusRef = useRef<UserPreferencesStatus>("loading");
    const preferencesRef = useRef<UserPreferences | null>(null);
    // Bumped whenever preferences are set from a save, so an older in-flight load does not overwrite them.
    const preferencesVersionRef = useRef(0);
    const loadPromiseRef = useRef<Promise<void> | null>(null);
    const lastLoadStartedAtRef = useRef(0);
    const mountedRef = useRef(false);
    const channelRef = useRef<BroadcastChannel | null>(null);

    const updateStatus = useCallback((next: UserPreferencesStatus) => {
        statusRef.current = next;
        setStatus(next);
    }, []);

    const applyPreferences = useCallback((next: UserPreferences) => {
        preferencesRef.current = next;
        setPreferences(next);
        updateStatus("ready");
    }, [updateStatus]);

    const load = useCallback((): Promise<void> => {
        if (loadPromiseRef.current) {
            return loadPromiseRef.current;
        }

        lastLoadStartedAtRef.current = Date.now();
        const run = (async () => {
            const userId = await waitForDatabaseUserId();
            if (!mountedRef.current) {
                return;
            }
            if (!userId) {
                if (statusRef.current !== "ready") {
                    updateStatus("unavailable");
                }
                return;
            }

            userIdRef.current = userId;
            const version = preferencesVersionRef.current;
            const [loadedPreferences, loadedRoles] = await Promise.all([
                GetUserPreferences(userId),
                rolesRef.current.length > 0 ? Promise.resolve(rolesRef.current) : GetAvailableRoles(),
            ]);
            if (!mountedRef.current) {
                return;
            }

            if (loadedRoles !== rolesRef.current) {
                rolesRef.current = loadedRoles;
                setRoles(loadedRoles);
            }

            if (loadedPreferences) {
                if (version === preferencesVersionRef.current) {
                    applyPreferences(loadedPreferences);
                }
            } else if (statusRef.current !== "ready") {
                // A failed background refresh keeps the preferences that were already loaded.
                updateStatus("unavailable");
            }
        })().finally(() => {
            loadPromiseRef.current = null;
        });

        loadPromiseRef.current = run;
        return run;
    }, [applyPreferences, updateStatus]);

    useEffect(() => {
        mountedRef.current = true;
        void load();

        const refresh = (force: boolean) => {
            const elapsed = Date.now() - lastLoadStartedAtRef.current;
            if (elapsed < MIN_REFRESH_GAP_MS) {
                return;
            }
            // Refresh eagerly while the role prompt may be showing, so a role picked in another tab closes it.
            const urgent = statusRef.current !== "ready" || preferencesRef.current?.needsRoleSelection === true;
            if (force || urgent || elapsed >= REFRESH_INTERVAL_MS) {
                void load();
            }
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                refresh(true);
            }
        };
        const handleFocus = () => refresh(false);

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);

        const channel = createBroadcastChannel();
        channelRef.current = channel;
        if (channel) {
            channel.onmessage = (event: MessageEvent<PreferencesBroadcast>) => {
                const broadcastPreferences = toUserPreferences(event.data?.preferences);
                if (broadcastPreferences && event.data.userId && event.data.userId === userIdRef.current) {
                    preferencesVersionRef.current += 1;
                    applyPreferences(broadcastPreferences);
                }
            };
        }

        return () => {
            mountedRef.current = false;
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", handleFocus);
            channel?.close();
            channelRef.current = null;
        };
    }, [applyPreferences, load]);

    const reload = useCallback(async () => {
        if (statusRef.current === "unavailable") {
            updateStatus("loading");
        }
        await load();
    }, [load, updateStatus]);

    const save = useCallback(async (update: UserPreferencesUpdate): Promise<UpdateUserPreferencesResult> => {
        const userId = userIdRef.current;
        if (!userId) {
            return { ok: false, error: UNAVAILABLE_MESSAGE };
        }

        const result = await UpdateUserPreferences(userId, update);
        if (result.ok && mountedRef.current) {
            preferencesVersionRef.current += 1;
            applyPreferences(result.preferences);
            try {
                channelRef.current?.postMessage({ userId, preferences: result.preferences } as PreferencesBroadcast);
            } catch (error) {
                console.warn("Failed to share the updated preferences with other tabs", error);
            }
        }
        return result;
    }, [applyPreferences]);

    const openSettings = useCallback(() => setIsSettingsOpen(true), []);
    const closeSettings = useCallback(() => setIsSettingsOpen(false), []);

    const value = useMemo(
        () => ({ preferences, roles, status, isSettingsOpen, openSettings, closeSettings, save, reload }),
        [preferences, roles, status, isSettingsOpen, openSettings, closeSettings, save, reload]
    );

    return <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>;
};

export const useUserPreferences = () => {
    const context = useContext(UserPreferencesContext);
    if (!context) {
        throw new Error("useUserPreferences must be used within a UserPreferencesProvider");
    }
    return context;
};
